import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiDownload,
  FiCopy,
  FiCheck,
  FiMessageSquare,
  FiFileText,
  FiHeart,
  FiBookOpen,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import PropTypes from "prop-types";
import {
  fetchLatestSnapshot,
  resolveConfigForFilters,
} from "../services/snapshotService";
import { fetchLeavesForGrade } from "../services/curriculumApiService";

const TRUNCATE_LENGTH = 100;

const formatValue = (val) => {
  if (Array.isArray(val)) {
    return val.join(", ");
  }
  if (typeof val === "object" && val !== null) {
    return JSON.stringify(val);
  }
  const str = String(val);
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed.join(", ");
    }
  } catch {
    // not JSON, use as-is
  }
  return str;
};

const ExpandableText = ({ text, maxLength = TRUNCATE_LENGTH }) => {
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded]);

  const handleToggle = () => {
    if (!expanded && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < 260;
      setPos({
        top: flipUp ? rect.top : rect.bottom + 4,
        left: rect.left,
        flipUp,
      });
    }
    setExpanded((prev) => !prev);
  };

  if (!text) {
    return <span className="text-gray-400">&mdash;</span>;
  }

  const displayText = formatValue(text);
  const needsTruncation = displayText.length > maxLength;

  if (!needsTruncation) {
    return <span className="line-clamp-1">{displayText}</span>;
  }

  return (
    <div>
      <div className="flex items-center gap-1">
        <span className="line-clamp-1 min-w-0 shrink">
          {displayText.slice(0, maxLength)}&hellip;
        </span>
        <button
          ref={btnRef}
          type="button"
          onClick={handleToggle}
          className="shrink-0 text-primary-600 hover:text-primary-700"
        >
          {expanded ? (
            <FiChevronUp className="size-3.5" />
          ) : (
            <FiChevronDown className="size-3.5" />
          )}
        </button>
      </div>
      {expanded && (
        <div
          ref={dropdownRef}
          className="fixed z-50 max-h-60 w-80 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 shadow-lg"
          style={{
            left: Math.min(pos.left, window.innerWidth - 340),
            ...(pos.flipUp
              ? { bottom: window.innerHeight - pos.top + 4 }
              : { top: pos.top }),
          }}
        >
          {displayText}
        </div>
      )}
    </div>
  );
};

ExpandableText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxLength: PropTypes.number,
};

// Grade label per curriculum. Display name is the raw DB curriculum string.
const curriculumConfig = {
  cc: { gradeLabel: "Grade" },
  teks: { gradeLabel: "Grade" },
  ap: { gradeLabel: "Unit" },
  sat: { gradeLabel: "Unit" },
  "athena-cc": { gradeLabel: "Grade" },
};

// Map URL slug back to the DB curriculum string. Slugs are produced by
// lowercasing + dash-replacing the curriculum name (see BenchmarkSets.toSlug).
const SLUG_TO_DB_CURRICULUM = {
  cc: "CC",
  ap: "AP",
  sat: "SAT",
  teks: "TEKS",
  "athena-cc": "Athena-CC",
};

const fromSlug = (slug) => {
  if (!slug) {
    return "CC";
  }
  return SLUG_TO_DB_CURRICULUM[slug.toLowerCase()] || slug.replace(/-/g, " ");
};

const getCurriculumInfo = (dbName) => {
  const display = dbName || "-";
  const gradeLabel =
    (dbName && curriculumConfig[dbName.toLowerCase()]?.gradeLabel) || "Grade";
  return { abbreviation: display, fullName: display, gradeLabel };
};

const TYPE_BADGE_CONFIG = {
  question: {
    label: "Questions",
    icon: FiMessageSquare,
    color: "text-blue-700",
    bg: "bg-blue-50",
  },
  article: {
    label: "Articles",
    icon: FiFileText,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
};

const MODIFIER_BADGE_CONFIG = {
  stimulus: {
    label: "With Stimulus",
    icon: FiBookOpen,
    color: "text-rose-700",
    bg: "bg-rose-50",
  },
  interests: {
    label: "With Interests",
    icon: FiHeart,
    color: "text-amber-700",
    bg: "bg-amber-50",
  },
};

/**
 * Simple deterministic hash from a string → integer.
 * Used to seed interest selection so the same leaf always gets the same interests.
 */
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

/**
 * Pick interests deterministically from the categorized pool.
 * Uses the seed (leaf ID) so the same leaf always gets the same combination.
 * For categorized pools, picks one interest from EVERY category. For flat
 * arrays, returns the whole pool deterministically shuffled.
 */
const pickRandomInterests = (pool, seed = "") => {
  if (!pool) {
    return [];
  }

  const h = hashCode(seed);

  if (Array.isArray(pool)) {
    return pool.map((_, i) => pool[(h + i * 7) % pool.length]);
  }

  return Object.keys(pool)
    .map((cat, i) => {
      const items = pool[cat];
      if (!items || items.length === 0) {
        return null;
      }
      const itemIdx = (h + i * 13) % items.length;
      return items[itemIdx];
    })
    .filter(Boolean);
};

/**
 * Expand a leaf's supportedFormats map { type: [difficulties] } into
 * a flat list of { type, difficulty } pairs.
 */
const expandLeafFormats = (supportedFormats) => {
  const pairs = [];
  for (const [type, difficulties] of Object.entries(supportedFormats)) {
    if (!difficulties || difficulties.length === 0) {
      pairs.push({ type, difficulty: null });
    } else {
      for (const difficulty of difficulties) {
        pairs.push({ type, difficulty });
      }
    }
  }
  return pairs;
};

/**
 * Generate the cross-product of leaves × (types × difficulties).
 *
 * Per-leaf variant resolution:
 *   - If the snapshot config row has explicit types (e.g. ["article"]),
 *     always use config.types × config.difficulties — supportedFormats is
 *     question-only metadata and must not override article runs.
 *   - If the config row has no explicit types (question runs) AND the leaf
 *     has supportedFormats (e.g. Athena-CC/Math), use those exact
 *     (type, difficulty) pairs declared per substandard.
 *   - Otherwise fall back to config.types × config.difficulties.
 *   - Empty config arrays = one request per leaf (no type/difficulty).
 *
 * If interestPool is provided, each request gets deterministic interests.
 */
const generateRequests = (
  leaves,
  config,
  grade,
  curriculum,
  subject,
  interestPool,
  modifierId
) => {
  const requests = [];
  const configTypes =
    config.types && config.types.length > 0 ? config.types : [null];
  const configDifficulties =
    config.difficulties && config.difficulties.length > 0
      ? config.difficulties
      : [null];

  for (const leaf of leaves) {
    const useLeafFormats =
      leaf.supportedFormats && (!config.types || config.types.length === 0);
    const variantPairs = useLeafFormats
      ? expandLeafFormats(leaf.supportedFormats)
      : configTypes.flatMap((type) =>
          configDifficulties.map((difficulty) => ({ type, difficulty }))
        );

    for (const { type, difficulty } of variantPairs) {
      const request = {
        curriculum,
        subject,
        grade,
        skills: {
          substandard_id: leaf.leafId,
          lesson_title: leaf.lessonName || "",
          substandard_description: leaf.description,
        },
      };
      if (type) {
        request.type = type;
      }
      if (difficulty) {
        request.difficulty = difficulty;
      }
      if (interestPool) {
        // Seed must match worker/src/services/prepareRequests.js so the
        // preview shows the same interests the worker will actually send.
        request.student_interests = pickRandomInterests(
          interestPool,
          `${leaf.leafId}-${type || ""}-${difficulty || ""}-${modifierId ?? ""}`
        );
      }
      requests.push(request);
    }
  }

  return requests;
};

const BenchmarkSetDetail = () => {
  const { curriculum: curriculumSlug, grade, subject } = useParams();
  const [searchParams] = useSearchParams();
  const curriculum = fromSlug(curriculumSlug);
  const curriculumInfo = getCurriculumInfo(curriculum);

  const categoryParam = searchParams.get("category");
  const modifierParam = searchParams.get("modifier");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const [leafCount, setLeafCount] = useState(0);

  // Scroll to top when component mounts or when grade/subject changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [grade, subject]);

  // Fetch dimension config + curriculum leaves and generate requests on-the-fly.
  // The `cancelled` flag guards against rapid navigation: if the user moves to
  // a different benchmark set before this load finishes, the in-flight result
  // must not overwrite state that belongs to the newer selection.
  useEffect(() => {
    let cancelled = false;
    const loadRequests = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Load latest snapshot — config + interest pool come from there
        const { data: snapshot, error: snapshotError } =
          await fetchLatestSnapshot();
        if (cancelled) {
          return;
        }
        if (snapshotError || !snapshot) {
          throw new Error(
            snapshotError?.message ||
              "No dataset snapshot found in public/snapshots/"
          );
        }

        // 2. Resolve the config row whose modifiers match the URL category/modifier
        const filters = {
          request_category: categoryParam || undefined,
          has_interests: modifierParam === "interests" ? true : undefined,
          has_stimulus: modifierParam === "stimulus" ? true : undefined,
        };
        const resolved = resolveConfigForFilters(
          snapshot,
          curriculum,
          subject,
          filters
        );
        if (!resolved) {
          throw new Error(
            `No matching config in snapshot v${snapshot.dataset_version} for ${curriculum}/${subject}`
          );
        }
        const { config, activeModifiers } = resolved;

        // 3. Fetch leaves from curriculum API (per-leaf supported_formats
        // lives there). courseName is the verbatim curriculum-repo course
        // string (e.g. "5th Grade: US History (1491-1850)") that we'll
        // surface as request.grade so previews/copies match what the
        // worker sends to the generator. Null for AP (uses unit number).
        const {
          data: leaves,
          error: leavesError,
          courseName: resolvedCourseName,
        } = await fetchLeavesForGrade(curriculum, subject, grade);
        if (cancelled) {
          return;
        }

        if (leavesError) {
          throw new Error(
            leavesError?.message || "Failed to fetch curriculum leaves"
          );
        }

        if (!leaves || leaves.length === 0) {
          setRequests([]);
          setLeafCount(0);
          setLoading(false);
          return;
        }

        setLeafCount(leaves.length);

        // 4. Pull interest pool from the snapshot if this is a "with interests" set
        const interestPool =
          modifierParam === "interests" ? snapshot.interest_pool : null;

        // The modifier ID is part of the worker's interest seed, so we have
        // to thread it through here too. activeModifiers is filtered to those
        // matching the URL's category/modifier; pick the first one (a "with
        // interests" view should resolve to exactly one modifier).
        const modifierId =
          interestPool && activeModifiers && activeModifiers.length > 0
            ? activeModifiers[0].id
            : null;

        // 4. Generate cross-product
        const generated = generateRequests(
          leaves,
          config,
          // Use the resolved curriculum-repo course name when available so
          // the preview matches what the worker sends to the generator.
          resolvedCourseName || grade,
          curriculum,
          subject,
          interestPool,
          modifierId
        );

        // Sort by substandard_id
        const sorted = [...generated].sort((a, b) => {
          const idA = a.skills?.substandard_id || "";
          const idB = b.skills?.substandard_id || "";
          return idA.localeCompare(idB);
        });

        if (!cancelled) {
          setRequests(sorted);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to generate benchmark requests");
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    loadRequests();
    return () => {
      cancelled = true;
    };
  }, [grade, subject, curriculum, categoryParam, modifierParam]);

  const hasLessonTitle = requests.some(
    (request) => request.skills?.lesson_title
  );
  const hasType = requests.some((request) => request.type);
  const hasDifficulty = requests.some((request) => request.difficulty);
  const hasStudentInterests = requests.some(
    (request) => request.student_interests
  );

  // Helper function to capitalize first letter
  const capitalize = (str) => {
    if (!str) {
      return "";
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const buildExportRequest = (request) => {
    const exportRequest = {
      grade: request.grade,
      subject: request.subject,
      quantity: 1,
      skills: {
        lesson_title: request.skills?.lesson_title || "",
        substandard_id: request.skills?.substandard_id || "",
        substandard_description: request.skills?.substandard_description || "",
      },
    };
    if (request.type) {
      exportRequest.type = request.type;
    }
    if (request.difficulty) {
      exportRequest.difficulty = request.difficulty;
    }
    if (request.student_interests) {
      exportRequest.student_interests = request.student_interests;
    }
    return exportRequest;
  };

  // Export to JSONL
  const handleExportJSON = () => {
    const jsonlLines = requests.map((request) =>
      JSON.stringify(buildExportRequest(request))
    );
    const jsonlContent = jsonlLines.join("\n");
    const dataBlob = new Blob([jsonlContent], { type: "application/jsonl" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(curriculumInfo.gradeLabel || "grade").toLowerCase()}-${grade}-${subject}-benchmark.jsonl`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Copy dataset to clipboard
  const handleCopyDataset = async (format) => {
    let content = "";

    switch (format) {
      case "jsonl": {
        content = requests
          .map((request) => JSON.stringify(buildExportRequest(request)))
          .join("\n");
        break;
      }

      case "json": {
        const jsonArray = requests.map((request) =>
          buildExportRequest(request)
        );
        content = JSON.stringify(jsonArray, null, 2);
        break;
      }

      case "csv": {
        const headers = [
          ...(hasLessonTitle ? ["Lesson Title"] : []),
          "Substandard ID",
          "Description",
          "Type",
          "Difficulty",
          "Grade",
          "Subject",
          ...(hasStudentInterests ? ["Student Interests"] : []),
        ];

        const rows = requests.map((request) => {
          const row = [
            ...(hasLessonTitle ? [request.skills?.lesson_title || ""] : []),
            request.skills?.substandard_id || "",
            request.skills?.substandard_description || "",
            request.type || "",
            request.difficulty || "",
            request.grade || "",
            request.subject || "",
            ...(hasStudentInterests
              ? [
                  Array.isArray(request.student_interests)
                    ? request.student_interests.join(", ")
                    : request.student_interests || "",
                ]
              : []),
          ];

          return row
            .map((value) => {
              const strValue = String(value);
              if (
                strValue.includes(",") ||
                strValue.includes('"') ||
                strValue.includes("\n")
              ) {
                return `"${strValue.replace(/"/g, '""')}"`;
              }
              return strValue;
            })
            .join(",");
        });

        content = [headers.join(","), ...rows].join("\n");
        break;
      }

      default:
        return;
    }

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setShowCopyMenu(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  // Get difficulty badge color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "hard":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get type badge color
  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "mcq":
        return "bg-blue-100 text-blue-800";
      case "fill-in":
        return "bg-purple-100 text-purple-800";
      case "mcq_set":
        return "bg-indigo-100 text-indigo-800";
      case "saq":
        return "bg-cyan-100 text-cyan-800";
      case "leq":
        return "bg-teal-100 text-teal-800";
      case "dbq":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/benchmark-sets"
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
        >
          <FiArrowLeft className="size-4" />
          <span>Back to Benchmark Sets</span>
        </Link>

        {/* Header */}
        <div className="mb-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                {curriculumInfo.gradeLabel || "Grade"} {grade} {subject}
              </h1>
              <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
                {curriculumInfo.fullName}
              </span>
              {leafCount > 0 && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {leafCount} leaves
                </span>
              )}
              {categoryParam &&
                TYPE_BADGE_CONFIG[categoryParam] &&
                (() => {
                  const cfg = TYPE_BADGE_CONFIG[categoryParam];
                  const Icon = cfg.icon;
                  return (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full ${cfg.bg} px-3 py-1 text-xs font-semibold ${cfg.color}`}
                    >
                      <Icon className="size-3.5" />
                      {cfg.label}
                    </span>
                  );
                })()}
              {modifierParam &&
                MODIFIER_BADGE_CONFIG[modifierParam] &&
                (() => {
                  const cfg = MODIFIER_BADGE_CONFIG[modifierParam];
                  const Icon = cfg.icon;
                  return (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full ${cfg.bg} px-3 py-1 text-xs font-semibold ${cfg.color}`}
                    >
                      <Icon className="size-3.5" />
                      {cfg.label}
                    </span>
                  );
                })()}
              {categoryParam && !modifierParam && (
                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  Simple
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {!loading && (
                <span className="text-sm font-medium text-gray-500">
                  {requests.length.toLocaleString()} Inputs
                </span>
              )}

              {/* Copy Button with Dropdown */}
              {!loading && requests.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowCopyMenu(!showCopyMenu)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                      copied
                        ? "border-green-600 bg-green-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
                    }`}
                  >
                    {copied ? (
                      <>
                        <FiCheck className="size-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <FiCopy className="size-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>

                  {showCopyMenu && (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setShowCopyMenu(false)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setShowCopyMenu(false);
                          }
                        }}
                        aria-label="Close menu"
                      />
                      <div className="absolute right-0 z-20 mt-2 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                        <div className="py-1">
                          <button
                            onClick={() => handleCopyDataset("jsonl")}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            Copy as JSONL
                          </button>
                          <button
                            onClick={() => handleCopyDataset("json")}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            Copy as JSON
                          </button>
                          <button
                            onClick={() => handleCopyDataset("csv")}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            Copy as CSV
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Export Button */}
              {!loading && requests.length > 0 && (
                <button
                  onClick={handleExportJSON}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-primary-700"
                >
                  <FiDownload className="size-4" />
                  <span>Export JSONL</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-12 text-center">
            <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-500">
              Generating requests from curriculum...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
            {error}
          </div>
        )}

        {/* Requests Table */}
        {!loading && !error && requests.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      colSpan={hasLessonTitle ? 3 : 2}
                      className="border-b border-gray-200 px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide text-gray-700"
                    >
                      Skills
                    </th>
                    {hasType && (
                      <th className="w-32 border-b border-l border-gray-200 px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide text-gray-700">
                        Type
                      </th>
                    )}
                    {hasDifficulty && (
                      <th className="w-28 border-b border-l border-gray-200 px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide text-gray-700">
                        Difficulty
                      </th>
                    )}
                    {hasStudentInterests && (
                      <th className="border-b border-l border-gray-200 px-6 py-4 text-center text-sm font-semibold uppercase tracking-wide text-gray-700">
                        Student Interests
                      </th>
                    )}
                  </tr>
                  <tr className="bg-gray-50">
                    {hasLessonTitle && (
                      <th className="border-b border-gray-200 px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                        Lesson Title
                      </th>
                    )}
                    <th className="border-b border-gray-200 px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                      Substandard ID
                    </th>
                    <th className="border-b border-gray-200 px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                      Description
                    </th>
                    {hasType && (
                      <th className="w-32 border-b border-l border-gray-200 px-6 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-600">
                        Type
                      </th>
                    )}
                    {hasDifficulty && (
                      <th className="w-28 border-b border-l border-gray-200 px-6 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-600">
                        Difficulty
                      </th>
                    )}
                    {hasStudentInterests && (
                      <th className="border-b border-l border-gray-200 px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-600">
                        Interests
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {requests.map((request, index) => (
                    <tr
                      key={index}
                      className="transition-colors hover:bg-gray-50"
                    >
                      {hasLessonTitle && (
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {request.skills?.lesson_title}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {request.skills?.substandard_id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <ExpandableText
                          text={request.skills?.substandard_description}
                        />
                      </td>
                      {hasType && (
                        <td className="w-32 border-l border-gray-200 px-6 py-4 text-center">
                          {request.type ? (
                            <span
                              className={`inline-block rounded px-2 py-1 text-xs font-medium uppercase ${getTypeColor(request.type)}`}
                            >
                              {request.type}
                            </span>
                          ) : (
                            <span className="text-gray-400">&mdash;</span>
                          )}
                        </td>
                      )}
                      {hasDifficulty && (
                        <td className="w-28 border-l border-gray-200 px-6 py-4 text-center">
                          {request.difficulty ? (
                            <span
                              className={`inline-block rounded px-2 py-1 text-xs font-medium ${getDifficultyColor(request.difficulty)}`}
                            >
                              {capitalize(request.difficulty)}
                            </span>
                          ) : (
                            <span className="text-gray-400">&mdash;</span>
                          )}
                        </td>
                      )}
                      {hasStudentInterests && (
                        <td className="max-w-xs border-l border-gray-200 px-6 py-4 text-sm text-gray-600">
                          {request.student_interests ? (
                            <div className="flex flex-wrap gap-1">
                              {request.student_interests.map(
                                (interest, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                                  >
                                    {interest}
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">&mdash;</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && requests.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              No curriculum leaves found for{" "}
              {curriculumInfo.gradeLabel || "Grade"} {grade} {subject}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BenchmarkSetDetail;
